import { ChevronLeft, X } from 'lucide-react';
import styles from './MyHomes.module.css';
import { useModal } from '../../hooks/modalContext';
import { AnimatePresence, motion } from 'framer-motion';
import PropertyData from './PropertyRNTData';
import { useEffect, useRef, useState } from 'react';
import PropertyManagerData from './PropertyManagerData';
import { getBuildingDetails, updateBuildingStatus } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { buildingDetails } from '../../utils/types';
import clsx from 'clsx';
import { formatDateToDisplayUTCMonth } from '../../utils/utils';
import { showToast } from '../../utils/showToast';

const MyHomes = () => {
  const { setModal, modalState } = useModal();
  const tenantBuildingId = modalState?.tenantBuildingId;
  const leaseEditFocus = modalState?.leaseEditFocus;
  const { auth } = useAuth();
  const [showRNT, setShowRNT] = useState(true);
  const [manualAddress, setManualAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState<buildingDetails[] | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const [isAutoCompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  // Derive from the selected building so the edit form always gets a real ID
  // as soon as the list loads (avoids empty Formik defaults on deep-link open).
  const activeBuildingId =
    building && activeIndex !== null
      ? String(building[activeIndex]?.building.tenantBuildingId ?? '')
      : '';

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const fetchBuildingDetails = async () => {
      try {
        setLoading(true);
        const response = await getBuildingDetails(auth?.data.token);
        setBuilding(response.data);
      } catch {
        console.error('Error fetching building details');
      } finally {
        setLoading(false);
      }
    };
    fetchBuildingDetails();
  }, []);
  useEffect(() => {
    if (!building?.length) return;

    if (tenantBuildingId) {
      const preselectedIndex = building.findIndex(
        (b) => String(b.building.tenantBuildingId) === String(tenantBuildingId)
      );
      setActiveIndex(preselectedIndex !== -1 ? preselectedIndex : 0);
      return;
    }

    setActiveIndex(0);
  }, [building, tenantBuildingId]);
  useEffect(() => {
    if (activeIndex !== null && cardRefs.current[activeIndex]) {
      cardRefs.current[activeIndex]?.scrollIntoView({
        behavior: 'smooth', // smooth scrolling
        block: 'nearest', // align to nearest edge
      });
    }
  }, [activeIndex, building]);

  const handleStatusToggle = async (tenantBuildingId: string, currentIsActive: boolean) => {
    if (updatingStatus) return;

    try {
      setUpdatingStatus(true);
      const newStatus = !currentIsActive;
      await updateBuildingStatus(auth?.data.token, tenantBuildingId, newStatus);

      showToast({
        message: newStatus
          ? 'Building has been successfully activated.'
          : 'Building has been successfully deactivated.',
        type: 'success',
      });
      setModal(null, { refreshProperties: true });
    } catch (error) {
      console.error('Error updating building status:', error);
      showToast({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to update building status. Please try again.',
        type: 'error',
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Determine if building is active - check isActive field first, fallback to removed === 0
  const getBuildingIsActive = (buildingItem: buildingDetails): boolean => {
    return buildingItem?.building?.isActive;
  };

  return (
    <motion.main
      className={styles.main}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.headingOuter}>
        <span> My homes</span>{' '}
        <X className={styles.crossIcon} size={24} onClick={() => setModal(null)} stroke='#0E0E0B' />
      </div>
      <div className={styles.contentOuter}>
        <div className={styles.leftSection}>
          {loading ? (
            <>
              <div className={styles.propertyCardLoading} />
              <div className={styles.propertyCardLoading} />
            </>
          ) : (
            <>
              {building?.map((item, index) => {
                const isActive = getBuildingIsActive(item);
                return (
                  <div
                    key={item.building.tenantBuildingId || index}
                    ref={(el) => (cardRefs.current[index] = el)}
                    className={clsx(styles.propertyCard, {
                      [styles.activeProperty]: activeIndex === index,
                      [styles.disabledCard]: !isActive,
                    })}
                    onClick={() => setActiveIndex(index)}
                  >
                    <div className={styles.nameOuter}>
                      <p
                        className={clsx(styles.name, {
                          [styles.activeName]: activeIndex === index,
                        })}
                      >
                        {item.building.buildingName}
                      </p>
                      <p className={styles.address}>
                        {item.address?.fullAddress || 'No address found'}
                      </p>
                    </div>
                    <div className={styles.nameOuter2}>
                      <p className={clsx(styles.name, styles.activeName)}>
                        ${item.building.rentAmount?.toLocaleString() || '-'}
                      </p>
                      <p className={styles.date}>
                        Due on{' '}
                        <span className='font-bold'>
                          {formatDateToDisplayUTCMonth(item.building.nextDueDate) || 'N/A'}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </>
          )}
          <button
            className={styles.addBtn}
            onClick={() => setModal('complete-building', { isNewBuilding: true })}
          >
            Add home +
          </button>
        </div>
        <div className={`${styles.rightSection} ${isAutoCompleteOpen && 'overflow-hidden'}`}>
          <AnimatePresence mode='wait'>
            {showRNT ? (
              <>
                <motion.div
                  key='rnt'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {manualAddress ? (
                    <div className={styles.propertyHead}>
                      <button onClick={() => setManualAddress(false)}>
                        <ChevronLeft />
                      </button>
                      <span>Enter address manually</span>
                    </div>
                  ) : (
                    <div className={styles.propertyHead}>
                      <p>Property details</p>
                      {activeIndex !== null && building?.[activeIndex] && (
                        <span
                          className={clsx(styles.statusBadge, {
                            [styles.statusBadgeActive]: getBuildingIsActive(building[activeIndex]),
                            [styles.statusBadgeInactive]: !getBuildingIsActive(
                              building[activeIndex]
                            ),
                          })}
                        >
                          {getBuildingIsActive(building[activeIndex]) ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </div>
                  )}
                  {activeIndex !== null && building?.[activeIndex] && (
                    <PropertyData
                      setShowRNT={setShowRNT}
                      setIsManualAddress={setManualAddress}
                      tenantBuildingId={activeBuildingId}
                      isManualAddress={manualAddress}
                      setIsAutoCompleteOpen={setIsAutocompleteOpen}
                      hasManager={building[activeIndex].tenantManager ? true : false}
                      buildingItem={building[activeIndex]}
                      onStatusToggle={handleStatusToggle}
                      isUpdatingStatus={updatingStatus}
                      getBuildingIsActive={getBuildingIsActive}
                    leaseEditFocus={leaseEditFocus}
                    />
                  )}
                </motion.div>
              </>
            ) : (
              <motion.div
                key='manager'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className={styles.propertyHead}>
                  <button onClick={() => setShowRNT(true)}>
                    <ChevronLeft />
                  </button>
                  <span>Property owner/manager details</span>
                </div>
                {activeIndex !== null && building?.[activeIndex] && (
                  <PropertyManagerData 
                    tenantBuildingId={activeBuildingId}
                    buildingItem={building[activeIndex]}
                    getBuildingIsActive={getBuildingIsActive}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.main>
  );
};
export default MyHomes;
